import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { getImpersonatedBy } from '@shared-ui/api';
import type { User, LoginCredentials, RegisterData } from '../types';
import * as authApi from '../api/auth';
import { deregisterPushDevice, startPushTokenSync } from '../api/push';
import { setStoredFeatureGates, setStoredExperiments } from '@shared-ui/feature-gates';
import { resetSession } from '@shared-ui/user-actions';
import { seedStoredLayout } from '../components/feedback/layoutPreference';
import { WrongAppError } from '@shared-ui/login';

/**
 * The server authenticated someone who is not a student. A teacher gets the way
 * on to the teacher app (front-door-target.md, "Wrong door"); anyone else the
 * plain refusal. Nothing is stored — the session is dropped either way.
 */
function refuseNonStudent(user: User, identifier?: string): never {
  if (user.roles?.includes('teacher')) throw new WrongAppError('teacher', identifier);
  throw new Error('This portal is for students only. Please use the teacher portal.');
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsRegistration: boolean;
  isImpersonated: boolean;
  /** True when the current session is an anonymous day-1 guest. */
  isGuest: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Start an anonymous day-1 guest session (deferred registration). */
  /** `link` is the practice link an arriving student followed, when there is
   *  one: the guest is then given that chapter's class, board and subject. */
  startGuest: (link?: { slug: string; channel?: string }) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  phoneLogin: (phone: string, verificationToken: string) => Promise<void>;
  phoneRegister: (name: string, phone: string, verificationToken: string, password?: string) => Promise<void>;
  googleLogin: (idToken: string, opts?: { native?: boolean }) => Promise<{ needsRegistration: boolean }>;
  appleLogin: (identityToken: string, fullName?: { givenName?: string; familyName?: string }) => Promise<void>;
  completeRegistration: (data: { name?: string; grade: number; targetExams?: string[]; primaryTargetExam?: string; subjects?: string[] }) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = authApi.getStoredUser();
    const storedToken = authApi.getStoredToken();

    if (storedUser && storedToken) {
      // Check for impersonation flag in token
      const impersonator = getImpersonatedBy(storedToken);
      if (impersonator) {
        setImpersonatedBy(impersonator);
      }

      // Restore session from localStorage immediately
      setUser(storedUser);

      // If cached user has incomplete data (e.g. impersonation with no name),
      // keep loading until background refresh completes
      const needsRefresh = !storedUser.name;
      if (!needsRefresh) {
        setIsLoading(false);
      }

      // Refresh user data in background (blocking if data is incomplete)
      authApi.verifyAndGetCurrentUser()
        .then((result) => {
          if (result.status === 'valid') {
            setUser(result.user);
            authApi.setStoredUser(result.user);
            // Refresh the cached gate/experiment maps too — otherwise a user who
            // registered (or had assignments change) wouldn't pick them up until
            // a full re-login. The profile endpoint is the universal backfill.
            if (result.user.featureGates) setStoredFeatureGates(result.user.featureGates);
            if (result.user.experiments) setStoredExperiments(result.user.experiments);
            seedStoredLayout(result.user.solutionAnalysisLayoutId);
          } else if (result.status === 'unauthorized') {
            // Token is definitively invalid (401) — clear session
            authApi.logout();
            setUser(null);
            setImpersonatedBy(null);
          }
          // For 'error' (network issues, etc.), keep the cached session
        })
        .finally(() => {
          if (needsRefresh) setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user, credentials.email);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    seedStoredLayout(response.user.solutionAnalysisLayoutId);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  const appleLogin = useCallback(async (identityToken: string, fullName?: { givenName?: string; familyName?: string }) => {
    const response = await authApi.appleAuth(identityToken, fullName);
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    seedStoredLayout(response.user.solutionAnalysisLayoutId);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  const startGuest = useCallback(async (link?: { slug: string; channel?: string }) => {
    const response = await authApi.guestSession(link);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    seedStoredLayout(response.user.solutionAnalysisLayoutId);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    const response = await authApi.register(data as RegisterData & { verificationToken: string });
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user, data.email);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    seedStoredLayout(response.user.solutionAnalysisLayoutId);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  // Passwordless phone sign-in (verified `login` OTP → session).
  const phoneLogin = useCallback(async (_phone: string, verificationToken: string) => {
    const response = await authApi.otpLogin(verificationToken);
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user, _phone);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  // Phone signup (name + optional password after phone OTP).
  const phoneRegister = useCallback(async (name: string, phone: string, verificationToken: string, password?: string) => {
    const response = await authApi.phoneRegister({ name, phone, verificationToken, password });
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user, phone);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    setUser(response.user);
    setImpersonatedBy(null);
  }, []);

  const googleLogin = useCallback(async (idToken: string, opts?: { native?: boolean }) => {
    // Native (iOS shell) tokens are minted for the iOS OAuth client → verify with
    // clientType 'ios'; the web GSI token verifies with 'web'.
    const response = opts?.native
      ? await authApi.googleAuthNative(idToken)
      : await authApi.googleSignIn(idToken);
    if (!response.user.roles?.includes('student')) refuseNonStudent(response.user);
    authApi.clearAllStorage();
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    if (response.user.featureGates) setStoredFeatureGates(response.user.featureGates);
    if (response.user.experiments) setStoredExperiments(response.user.experiments);
    seedStoredLayout(response.user.solutionAnalysisLayoutId);
    setUser(response.user);
    setImpersonatedBy(null);
    return { needsRegistration: response.user.registrationComplete === false };
  }, []);

  // Sync any device token the shell hands over unprompted. It silently
  // re-registers on each launch once permission is granted, so this is what
  // repairs stale server-side device state — registration is the only moment
  // the backend hears about an install. No-ops outside the native shell.
  useEffect(() => {
    if (!user) return;
    return startPushTokenSync();
  }, [user]);

  const logout = useCallback(() => {
    // Also before clearing the token: the trace flush is user-authed, and the
    // server attributes it by token. Flushing after logout would either fail or
    // land under the next person to sign in on this device.
    resetSession('auth');
    // Before clearing the token — the DELETE needs it. Non-blocking, and the
    // backend reclaims the install on the next registration anyway, so a failure
    // here is not a hole. See docs/FeatureDesigns/push-notifications.md §3.3.
    deregisterPushDevice();
    authApi.logout();
    setUser(null);
    setImpersonatedBy(null);
  }, []);

  const completeRegistration = useCallback(async (data: { name?: string; grade: number; targetExams?: string[]; primaryTargetExam?: string; subjects?: string[] }) => {
    const response = await authApi.completeStudentRegistration(data);
    authApi.setStoredToken(response.token);
    authApi.setStoredUser(response.user);
    setUser(response.user);
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    authApi.setStoredUser(updatedUser);
    setUser(updatedUser);
  }, []);

  // A guest (registrationComplete:false + isGuest:true) does the day-1 aha FIRST,
  // so it must NOT be treated as needing registration (that would bounce it to
  // /complete-registration). Promotion clears isGuest → then it needs registration.
  const isGuest = !!user?.isGuest;
  const needsRegistration = user?.registrationComplete === false && !isGuest;
  const isImpersonated = !!impersonatedBy;

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    needsRegistration,
    isImpersonated,
    isGuest,
    login,
    startGuest,
    register,
    phoneLogin,
    phoneRegister,
    googleLogin,
    appleLogin,
    completeRegistration,
    logout,
    updateUser,
  }), [user, isLoading, needsRegistration, isImpersonated, isGuest, login, startGuest, register, phoneLogin, phoneRegister, googleLogin, appleLogin, completeRegistration, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
